import React from "react";
import { Card, CardContent, Box, Typography, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

const useStyles = makeStyles((theme) => ({
  resultCard: {
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
    },
  },
  cardSuccess: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
    backgroundColor: theme.palette.success.lighter || "#E8F5E9",
  },
  cardWarning: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.warning.lighter || "#FFF3E0",
  },
  cardError: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.error.lighter || "#FFEBEE",
  },
  cardNeutral: {
    borderLeft: `4px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  cardNumber: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
  },
  cardLabel: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  cardDescription: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

/**
 * Props del componente
 * @param {String} tipo - Tipo de categoría ('perfectos', 'buenos', 'debiles', 'sinMatch', 'errores')
 * @param {String} titulo - Título a mostrar
 * @param {String} descripcion - Descripción corta
 * @param {Number} cantidad - Cantidad de movimientos
 * @param {Number} monto - Monto total (opcional)
 * @param {Number} scorePromedio - Score promedio (opcional)
 * @param {ReactElement} icono - Ícono a mostrar
 * @param {String} colorIcono - Color del ícono
 * @param {Function} onClick - Handler al hacer click
 * @param {Boolean} modoMarchaBlanca - Si está en modo marcha blanca (solo para perfectos)
 */
export default function CardCategoria({
  tipo,
  titulo,
  descripcion,
  cantidad,
  monto,
  scorePromedio,
  icono,
  colorIcono,
  onClick,
  modoMarchaBlanca = false,
}) {
  const classes = useStyles();

  /**
   * Determina la clase CSS según el tipo de categoría
   */
  const getCardClass = () => {
    switch (tipo) {
      case "perfectos":
        return classes.cardSuccess;
      case "buenos":
        return classes.cardWarning;
      case "debiles":
      case "sinMatch":
        return classes.cardError;
      case "errores":
        return classes.cardNeutral;
      default:
        return classes.cardNeutral;
    }
  };

  /**
   * Formatea el monto en millones
   */
  const formatearMontoMillones = (monto) => {
    if (!monto || monto === 0) return "$ 0M";
    return `$ ${(monto / 1000000).toFixed(2)}M`;
  };

  return (
    <Grid item xs={12} md={6}>
      <Card
        className={`${classes.resultCard} ${getCardClass()}`}
        elevation={2}
        onClick={onClick}
      >
        <CardContent>
          <Box textAlign="center">
            {/* Ícono */}
            <Box className={classes.cardIcon} style={{ color: colorIcono }}>
              {icono}
            </Box>

            {/* Cantidad */}
            <Typography className={classes.cardNumber}>{cantidad}</Typography>

            {/* Título */}
            <Typography className={classes.cardLabel}>{titulo}</Typography>

            {/* Descripción */}
            <Typography className={classes.cardDescription}>
              {descripcion}
            </Typography>

            {/* Información adicional (monto y score) */}
            {cantidad > 0 && (
              <Box
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Grid container spacing={1}>
                  {/* Monto Total */}
                  {monto !== undefined && (
                    <Grid item xs={scorePromedio !== undefined ? 6 : 12}>
                      <Typography variant="caption" color="textSecondary">
                        Monto Total
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {formatearMontoMillones(monto)}
                      </Typography>
                    </Grid>
                  )}

                  {/* Score Promedio */}
                  {scorePromedio !== undefined && (
                    <Grid item xs={monto !== undefined ? 6 : 12}>
                      <Typography variant="caption" color="textSecondary">
                        Score Prom.
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 600,
                          color:
                            scorePromedio >= 90
                              ? "#4caf50"
                              : scorePromedio >= 70
                                ? "#ff9800"
                                : "#f44336",
                        }}
                      >
                        {scorePromedio} pts
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Indicador especial para modo marcha blanca */}
            {modoMarchaBlanca && tipo === "perfectos" && cantidad > 0 && (
              <Box
                style={{
                  marginTop: 12,
                  padding: "8px 12px",
                  backgroundColor: "rgba(33, 150, 243, 0.1)",
                  borderRadius: 4,
                  border: "1px solid #2196f3",
                }}
              >
                <Typography
                  variant="caption"
                  style={{ color: "#1565c0", fontWeight: 600 }}
                >
                  🚀 Listos para auto-conciliar
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}
